import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, waLink } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Clock, Info, CalendarDays } from "lucide-react";

function fmtLocalDate(d) {
  if (!d) return "";
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [makes, setMakes] = useState({});
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [availability, setAvailability] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [make, setMake] = useState(params.get("make") || "");
  const [model, setModel] = useState(params.get("model") || "");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/car-makes").then((r) => setMakes(r.data));
  }, []);

  const svc = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);

  // Fetch availability whenever service + date change
  useEffect(() => {
    setSlot("");
    setAvailability(null);
    if (!serviceId || !date) return;
    setLoadingAvail(true);
    api.get("/bookings/availability", { params: { service_id: serviceId, date: fmtLocalDate(date) } })
      .then((r) => setAvailability(r.data))
      .catch(() => toast.error("Could not load availability"))
      .finally(() => setLoadingAvail(false));
  }, [serviceId, date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!serviceId || !make || !model || !date || !name || !phone) {
      toast.error("Please complete all fields");
      return;
    }
    const isHourly = availability?.mode === "hourly";
    if (isHourly && !slot) { toast.error("Pick an available time slot"); return; }
    if (!isHourly && availability && availability.available === false) {
      toast.error("This service isn't available on the selected date");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        service_id: serviceId,
        car_make: make, car_model: model,
        date: fmtLocalDate(date),
        time_slot: isHourly ? slot : (availability?.start_time || "10:00"),
        name, phone, notes,
      });
      toast.success(`Booked! Reference ${res.data.id}`);
      setTimeout(() => navigate(`/track?bookingId=${res.data.id}`), 400);
    } catch (err) {
      const msg = err.response?.data?.detail || "Booking failed";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const availableCount = availability?.mode === "hourly"
    ? availability.slots.filter((s) => s.available).length
    : (availability?.available ? 1 : 0);

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="booking-page">
      <div className="overline text-[#FFB800]">Book Appointment</div>
      <h1 className="font-display text-4xl mt-2">Reserve your slot.</h1>
      <p className="text-zinc-400 text-sm mt-3 max-w-2xl">Studio hours 10:00–20:00 IST · every day. Slots update in real-time based on studio capacity.</p>

      <form onSubmit={submit} className="mt-10 grid md:grid-cols-5 gap-8" data-testid="booking-form">
        {/* Left: service + calendar + slots */}
        <div className="md:col-span-3 space-y-6">
          <div className="card-surface rounded-lg p-6">
            <div className="overline mb-2">1. Choose service</div>
            <Select value={serviceId} onValueChange={setServiceId}>
              <SelectTrigger className="bg-[#0A0A0C] border-zinc-800 h-11" data-testid="book-service">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="bg-[#141417] border-zinc-800 text-zinc-100 max-h-72">
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`svc-opt-${s.id}`}>
                    {s.name} · {s.duration_display}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {svc && (
              <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                <div><div className="overline">Category</div><div className="capitalize mt-1">{svc.category}</div></div>
                <div><div className="overline">Duration</div><div className="mt-1">{svc.duration_display}</div></div>
                <div><div className="overline">Price</div><div className="mt-1 font-mono text-[#FFB800]">₹{svc.price?.toLocaleString()}</div></div>
              </div>
            )}
          </div>

          <div className="card-surface rounded-lg p-6">
            <div className="overline mb-3">2. Pick a date</div>
            <div className="rounded-md border border-zinc-800 p-2 inline-block">
              <Calendar mode="single" selected={date} onSelect={setDate}
                disabled={{ before: new Date() }} data-testid="booking-calendar" />
            </div>
          </div>

          {serviceId && date && (
            <div className="card-surface rounded-lg p-6" data-testid="availability-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="overline">3. Available times</div>
                {loadingAvail && <Loader2 className="h-4 w-4 animate-spin text-[#FFB800]" />}
              </div>
              {!loadingAvail && availability?.mode === "hourly" && (
                <>
                  <div className="text-xs text-zinc-500 mb-3">
                    {availableCount === 0 ? "No slots available for this date." :
                     `${availableCount} slot${availableCount === 1 ? "" : "s"} available · capacity ${availability.capacity} concurrent`}
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {availability.slots.map((s) => (
                      <button type="button" key={s.time}
                        disabled={!s.available}
                        onClick={() => setSlot(s.time)}
                        data-testid={`slot-${s.time}`}
                        className={`py-2 rounded-md border text-sm transition-colors font-mono
                          ${!s.available ? "border-zinc-900 text-zinc-700 line-through cursor-not-allowed" :
                            slot === s.time ? "border-[#FFB800] bg-[#FFB800]/10 text-[#FFB800]" :
                            "border-zinc-800 text-zinc-300 hover:border-zinc-500"}`}>
                        {s.time}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {!loadingAvail && availability?.mode === "daily" && (
                <div className="space-y-3" data-testid="daily-availability">
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarDays className="h-4 w-4 text-[#FFB800]" />
                    <span>Starts {availability.start_time} · Ends {availability.end_date} {availability.end_time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-[#FFB800]" />
                    <span>Duration: {availability.duration_display} · Requires {availability.days_required} day(s) of studio time</span>
                  </div>
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm
                    ${availability.available ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/5" : "border-red-800 text-red-400 bg-red-950/20"}`}
                    data-testid="daily-status">
                    {availability.available ? "Slot open for booking" : "Fully booked on this date — pick another"}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: contact + submit */}
        <div className="md:col-span-2 card-surface rounded-lg p-6 space-y-4 md:sticky md:top-24 h-fit">
          <div className="overline">Your details</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">Make</label>
              <Select value={make} onValueChange={(v) => { setMake(v); setModel(""); }}>
                <SelectTrigger data-testid="book-make" className="bg-[#0A0A0C] border-zinc-800 h-10"><SelectValue placeholder="Make" /></SelectTrigger>
                <SelectContent className="bg-[#141417] border-zinc-800 max-h-64">
                  {Object.keys(makes).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline block mb-2">Model</label>
              <Select value={model} onValueChange={setModel} disabled={!make}>
                <SelectTrigger data-testid="book-model" className="bg-[#0A0A0C] border-zinc-800 h-10"><SelectValue placeholder="Model" /></SelectTrigger>
                <SelectContent className="bg-[#141417] border-zinc-800 max-h-64">
                  {(makes[make] || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Your Name</label>
            <Input data-testid="book-name" className="bg-[#0A0A0C] border-zinc-800 h-10" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="overline block mb-2">Phone</label>
            <Input data-testid="book-phone" className="bg-[#0A0A0C] border-zinc-800 h-10" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91..." />
          </div>
          <div>
            <label className="overline block mb-2">Notes</label>
            <Textarea data-testid="book-notes" className="bg-[#0A0A0C] border-zinc-800" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {svc && (
            <div className="rounded-md border border-zinc-800 p-3 text-sm space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Service</span>
                <span>{svc.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Estimate</span>
                <span className="font-mono text-[#FFB800]">₹{svc.price?.toLocaleString()}</span>
              </div>
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full justify-center disabled:opacity-60"
            data-testid="book-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Booking"}
          </button>
          <a href={waLink(`Hi Tech Detailers, I'd like help with an appointment.`)} target="_blank" rel="noopener noreferrer" className="btn-outline w-full justify-center text-sm" data-testid="book-wa">
            Need help? WhatsApp us
          </a>
          <div className="flex items-start gap-2 text-xs text-zinc-500">
            <Info className="h-3.5 w-3.5 mt-0.5" />
            <span>Ceramic / Graphene / PPF services run one at a time. General services allow up to 2 vehicles concurrently.</span>
          </div>
        </div>
      </form>
    </div>
  );
}
